import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadStateApiService } from '../services/lead-state-api.service';
import { LeadStateRequest, LeadStateResponse } from '../models/lead-state.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-lead-state-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './lead-state-form.component.html'
})
export class LeadStateFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  stateId: number | null = null;

  // Form model. displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  name = '';
  private originalName = '';
  description = '';
  colorHex = '';
  isActive = true;

  // Duplicate validation
  checkingDuplicate = false;
  duplicateNameError = '';

  // Input validation
  nameValidationError = '';
  descriptionValidationError = '';

  readonly NAME_MAX = 30;
  readonly DESCRIPTION_MAX = 500;

  private readonly ALPHANUMERIC_REGEX = /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑ]+$/;

  onNameChange(value: string): void {
  const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
  const filtered = lettersOnly.substring(0, this.NAME_MAX);

  // Inc 16: mensaje por campo claro según el motivo del recorte.
  if (lettersOnly.length > this.NAME_MAX) {
    this.nameValidationError = `El nombre no puede exceder ${this.NAME_MAX} caracteres.`;
  } else if (lettersOnly !== value) {
    this.nameValidationError = 'Solo se permiten letras';
  } else {
    this.nameValidationError = '';
  }

  setTimeout(() => {
    this.name = filtered;
  });
}

  onDescriptionChange(value: string): void {
   let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
   const truncated = filtered.substring(0, this.DESCRIPTION_MAX);

   // INC 16: 500-char cap with a specific error so the user knows exactly
   // what's wrong instead of just "Validation error".
   if (filtered.length > this.DESCRIPTION_MAX) {
    this.descriptionValidationError = `La descripción no puede exceder ${this.DESCRIPTION_MAX} caracteres.`;
   } else if (filtered !== value) {
    this.descriptionValidationError = 'Solo se permiten letras';
   } else {
    this.descriptionValidationError = '';
  }

  filtered = truncated;

  // delay mínimo para forzar override de ngModel
   setTimeout(() => {
    this.description = filtered;
  });
}

  constructor(
    private stateApi: LeadStateApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.stateId = +id;
      this.cargarEstado(this.stateId);
    }
  }

  private cargarEstado(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.stateApi.getStateById(id, businessId).subscribe({
      next: (estado) => {
        this.name = estado.name;
        this.originalName = estado.name;
        this.description = estado.description || '';
        this.colorHex = estado.colorHex || '';
        this.isActive = estado.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el tipo';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Tipo de Lead' : 'Nuevo Tipo de Lead';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }
    // Inc 16: no continuar si hay errores de validación por campo.
    if (this.nameValidationError || this.descriptionValidationError) {
      this.error = 'Corrige los errores del formulario antes de guardar.';
      return;
    }

    this.error = '';
    this.duplicateNameError = '';
    this.checkingDuplicate = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.stateApi.getStates(businessId, 0, 200).pipe(
      map(states => states || []),
      catchError(() => of([] as LeadStateResponse[]))
    ).subscribe(existingStates => {
      this.checkingDuplicate = false;

      const normalizedName = this.name.trim().toLowerCase();
      const duplicateName = existingStates.find(s =>
        s.name.toLowerCase() === normalizedName &&
        (!this.isEditing || s.id !== this.stateId)
      );

      if (duplicateName) {
        this.duplicateNameError = `Ya existe un tipo con el nombre "${duplicateName.name}"`;
        return;
      }

      this.executeSave();
    });
  }

  private executeSave(): void {
    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const request: LeadStateRequest = {
      businessId,
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      colorHex: this.colorHex.trim() || undefined,
      isActive: this.isActive
    };

    if (this.isEditing && this.stateId) {
      this.stateApi.updateState(this.stateId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Tipo de lead',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/tipos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el tipo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.stateApi.createState(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Tipo de lead', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/estados']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el tipo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-leads/tipos']);
  }
}
